import React, { useEffect, useState, useRef } from 'react';
import { Plus, Package, Camera, X, Edit2, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import ArchivioImmagini from './ArchivioImmagini';

const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/api$/, '');

const emptyForm = {
  nome: '', category_id: '', supplier_id: '', descrizione: '', personalizzabile: false,
  prezzo_vendita_ivato: '', prezzo_acquisto_imponibile: '', iva_percentuale: 22,
  taglie_attive: [],
};

export default function ProductsCatalog() {
  const { user } = useAuth();
  const puoCreare     = puoFare(user, 'prodotti', 'create');
  const puoModificare = puoFare(user, 'prodotti', 'edit');
  const puoEliminare  = puoFare(user, 'prodotti', 'delete');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState(emptyForm);
  // Galleria del prodotto aperto: [{id, url, nome}] dall'archivio, la prima è la copertina.
  const [galleria, setGalleria] = useState([]);
  const [galleriaIniziale, setGalleriaIniziale] = useState('');
  const [carico, setCarico] = useState(null); // null | 'n di m'
  const [archivio, setArchivio] = useState(null);     // null | 'scegli' | 'gestisci'
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const [pr, ca, su] = await Promise.all([api.get('/products'), api.get('/categories'), api.get('/suppliers')]);
    setProducts(pr.data);
    setCategories(ca.data);
    setSuppliers(su.data);
  };

  useEffect(() => { load(); }, []);

  const catSizes = (category_id) => {
    const cat = categories.find(c => c.id === parseInt(category_id));
    return cat?.sizeGroups?.map(sg => sg.taglia) || [];
  };

  const handleCatChange = (category_id) => {
    const taglie = catSizes(category_id);
    setForm(f => ({ ...f, category_id, taglie_attive: taglie }));
  };

  const toggleTaglia = (t) => setForm(f => ({
    ...f,
    taglie_attive: f.taglie_attive.includes(t) ? f.taglie_attive.filter(x => x !== t) : [...f.taglie_attive, t],
  }));

  const leggi = (file) => new Promise((ok, ko) => { const r = new FileReader(); r.onload = ev => ok(ev.target.result); r.onerror = ko; r.readAsDataURL(file); });
  // Le foto nuove vanno subito nell'archivio (restano lì comunque) e si aggiungono in fondo alla galleria.
  const handleImgSelect = async (e) => {
    const files = [...e.target.files]; e.target.value = ''; if (!files.length) return;
    const falliti = [];
    for (let k = 0; k < files.length; k++) {
      setCarico(`${k + 1} di ${files.length}`);
      try {
        const r = await api.post('/products/immagini', { imageBase64: await leggi(files[k]), nome: files[k].name.replace(/\.[^.]+$/, '') });
        setGalleria(g => g.some(x => x.id === r.data.id) ? g : [...g, { id: r.data.id, url: r.data.url, nome: r.data.nome }]);
      } catch (err) { falliti.push(`${files[k].name}: ${err.response?.data?.error || 'errore'}`); }
    }
    setCarico(null);
    if (falliti.length) alert('Non caricate:\n' + falliti.join('\n'));
  };
  const spostaFoto = (k, d) => setGalleria(g => { const n = [...g]; const t = k + d; if (t < 0 || t >= n.length) return g; [n[k], n[t]] = [n[t], n[k]]; return n; });

  const openNew = () => {
    setEditProduct(null); setForm(emptyForm); setGalleria([]); setGalleriaIniziale('[]'); setShowPanel(true);
  };

  const openEdit = (p) => {
    setEditProduct(p);
    setForm({
      nome: p.nome, category_id: p.category_id || '', supplier_id: p.supplier_id || '',
      descrizione: p.descrizione || '', personalizzabile: p.personalizzabile,
      prezzo_vendita_ivato: p.prezzo_vendita_ivato,
      prezzo_acquisto_imponibile: p.prezzo_acquisto_imponibile,
      iva_percentuale: p.iva_percentuale,
      taglie_attive: p.variants.filter(v => v.attiva).map(v => v.taglia),
    });
    const g = p.galleria || [];
    setGalleria(g); setGalleriaIniziale(JSON.stringify(g.map(x => x.id))); setShowPanel(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      let product;
      if (editProduct) { product = (await api.put(`/products/${editProduct.id}`, form)).data; }
      else { product = (await api.post('/products', form)).data; }
      const ids = galleria.map(x => x.id);
      if (JSON.stringify(ids) !== galleriaIniziale) await api.put(`/products/${product.id}/galleria`, { immagine_ids: ids });
      setShowPanel(false); load();
    } catch (err) { alert(err.response?.data?.error || 'Errore'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Disattivare prodotto?')) return;
    await api.delete(`/products/${id}`); load();
  };

  const margine = (p) => {
    const costo = p.prezzo_acquisto_imponibile * (1 + p.iva_percentuale / 100);
    return ((p.prezzo_vendita_ivato - costo) / p.prezzo_vendita_ivato * 100).toFixed(1);
  };

  const totalStock = (p) => p.variants?.reduce((s, v) => s + (v.inventory?.quantita_disponibile || 0), 0) || 0;

  const filtered = products.filter(p =>
    p.nome.toLowerCase().includes(search.toLowerCase()) ||
    p.sku_base.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = filtered.reduce((g, p) => {
    const k = p.category?.nome || 'Senza categoria';
    if (!g[k]) g[k] = [];
    g[k].push(p); return g;
  }, {});

  return (
    <div style={{ display: 'flex', gap: '20px' }}>
      {/* MAIN */}
      <div style={{ flex: 1 }}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>CATALOGO PRODOTTI</h1>
            <p style={s.sub}>{products.length} prodotti · {products.reduce((a, p) => a + totalStock(p), 0)} pz in magazzino</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button style={s.btnSec} onClick={() => setArchivio('gestisci')}><Camera size={15} /> ARCHIVIO IMMAGINI</button>
            {puoCreare && <button style={s.btnPrimary} onClick={openNew}><Plus size={15} /> NUOVO PRODOTTO</button>}
          </div>
        </div>

        <input style={s.search} placeholder="Cerca per nome o SKU..." value={search} onChange={e => setSearch(e.target.value)} />

        {Object.entries(grouped).map(([cat, prods]) => (
          <div key={cat} style={{ marginBottom: '20px' }}>
            <div style={s.catHeader} onClick={() => setCollapsed(c => ({ ...c, [cat]: !c[cat] }))}>
              {collapsed[cat] ? <ChevronRight size={14} color="#F4600C" /> : <ChevronDown size={14} color="#F4600C" />}
              <span style={s.catName}>{cat}</span>
              <span style={s.catCount}>{prods.length} prodotti</span>
            </div>
            {!collapsed[cat] && (
              <div style={s.grid}>
                {prods.map(p => {
                  const img = p.immagine_url ? `${API_BASE}${p.immagine_url}` : null;
                  const mgn = margine(p);
                  const stock = totalStock(p);
                  return (
                    <div key={p.id} style={s.card}>
                      <div style={s.cardImg}>
                        {img ? <img src={img} alt={p.nome} style={s.cardImgEl} />
                          : <div style={s.cardImgPh}><Package size={28} color="var(--text-dim)" /></div>}
                        {p.personalizzabile && <div style={s.badge}>STAMPA</div>}
                        {p.galleria?.length > 1 && <div style={{ ...s.badge, left: 'auto', right: '8px', top: 'auto', bottom: '8px', background: 'rgba(0,0,0,.6)' }}>{p.galleria.length} FOTO</div>}
                      </div>
                      <div style={s.cardBody}>
                        <div style={s.cardTop}>
                          <span style={s.sku}>{p.sku_base}</span>
                          <span style={{ color: stock > 0 ? '#22C55E' : '#EF4444', fontSize: 'var(--font-size-xs)', fontWeight: '700' }}>{stock} pz</span>
                        </div>
                        <div style={s.prodName}>{p.nome}</div>
                        {p.supplier && <div style={{ fontSize: 'var(--font-size-xs)', color:'#F59E0B', fontWeight:'700', marginBottom:'6px' }}>🏭 {p.supplier.ragione_sociale}</div>}
                        <div style={s.prices}>
                          <div><div style={s.priceL}>VENDITA</div><div style={s.priceV}>€{p.prezzo_vendita_ivato.toFixed(2)}</div></div>
                          <div><div style={s.priceL}>ACQUISTO</div><div style={{ ...s.priceV, fontSize: 'var(--font-size-sm)', color: 'var(--text-dim)' }}>€{p.prezzo_acquisto_imponibile.toFixed(2)}</div></div>
                          <div><div style={s.priceL}>MARGINE</div><div style={{ ...s.priceV, fontSize: 'var(--font-size-sm)', color: parseFloat(mgn) > 30 ? '#22C55E' : '#F59E0B' }}>{mgn}%</div></div>
                        </div>
                        <div style={s.variants}>
                          {p.variants?.filter(v => v.attiva).map(v => (
                            <div key={v.id} style={{ ...s.varChip, opacity: (v.inventory?.quantita_disponibile || 0) === 0 ? 0.4 : 1 }}>
                              <span style={s.varT}>{v.taglia}</span>
                              <span style={s.varQ}>{v.inventory?.quantita_disponibile || 0}</span>
                            </div>
                          ))}
                        </div>
                        <div style={s.actions}>
                          {puoModificare && <button style={{ ...s.actionBtn, color: p.visibile_catalogo ? '#22C55E' : '#EF4444' }}
                            title={p.visibile_catalogo ? 'Visibile ai clienti — clicca per nascondere' : 'Nascosto ai clienti — clicca per mostrare'}
                            onClick={async () => { await api.put(`/products/${p.id}`, { visibile_catalogo: !p.visibile_catalogo }); load(); }}>
                            {p.visibile_catalogo ? '👁 VISIBILE' : '🚫 NASCOSTO'}
                          </button>}
                          {puoModificare && <button style={s.actionBtn} onClick={() => openEdit(p)}><Edit2 size={12} /> MODIFICA</button>}
                          {puoEliminare && <button style={{ ...s.actionBtn, color: '#EF4444' }} onClick={() => handleDelete(p.id)}><Trash2 size={12} /></button>}
                        </div>
                        {puoModificare && p.visibile_catalogo && (
                          <button style={{ ...s.actionBtn, width:'100%', marginTop:'6px', color: p.acquistabile ? '#22C55E' : '#F59E0B' }}
                            title={p.acquistabile ? 'Acquistabile — clicca per bloccare gli ordini su questo articolo' : 'Non acquistabile — visibile ma il cliente non può ordinarlo'}
                            onClick={async () => { await api.put(`/products/${p.id}`, { acquistabile: !p.acquistabile }); load(); }}>
                            {p.acquistabile ? '🛒 ACQUISTABILE' : '⛔ NON ACQUISTABILE'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && <div style={s.empty}><Package size={40} color="var(--text-dim)" /><p>Nessun prodotto</p></div>}
      </div>

      {archivio && (
        <ArchivioImmagini
          modo={archivio}
          puoCaricare={puoModificare || puoCreare}
          puoEliminare={puoEliminare}
          apiBase={API_BASE}
          onChiudi={() => setArchivio(null)}
          scelte={galleria.map(x => x.id)}
          onScegli={(img) => setGalleria(g => g.some(x => x.id === img.id) ? g.filter(x => x.id !== img.id) : [...g, { id: img.id, url: img.url, nome: img.nome }])}
        />
      )}

      {/* SIDE PANEL */}
      {showPanel && (
        <div style={s.panel}>
          <div style={s.panelHeader}>
            <span style={s.panelTitle}>{editProduct ? 'MODIFICA PRODOTTO' : 'NUOVO PRODOTTO'}</span>
            <button style={s.closeBtn} onClick={() => setShowPanel(false)}><X size={16} /></button>
          </div>
          <div style={s.panelBody}>
            {/* FOTO (galleria) */}
            <div style={s.imgUpload} onClick={() => !galleria.length && fileRef.current.click()}>
              {galleria.length ? <img src={`${API_BASE}${galleria[0].url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <Camera size={24} color="var(--text-dim)" /><span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)' }}>Carica foto</span>
                  </div>}
            </div>
            {galleria.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                {galleria.map((f, k) => (
                  <div key={f.id} title={f.nome} style={{ width: '64px', border: k === 0 ? '2px solid #F4600C' : '1px solid var(--border2)', borderRadius: '8px', overflow: 'hidden', background: 'var(--bg-input)' }}>
                    <img src={`${API_BASE}${f.url}`} alt="" style={{ width: '100%', height: '48px', objectFit: 'cover', display: 'block' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                      <button type="button" style={s.fotoBtn} disabled={k === 0} onClick={() => spostaFoto(k, -1)}>‹</button>
                      <button type="button" style={{ ...s.fotoBtn, color: '#EF4444' }} title="Togli dalle foto (resta in archivio)" onClick={() => setGalleria(g => g.filter(x => x.id !== f.id))}>✕</button>
                      <button type="button" style={s.fotoBtn} disabled={k === galleria.length - 1} onClick={() => spostaFoto(k, 1)}>›</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }} onChange={handleImgSelect} />
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '8px 0 4px' }}>
              <button type="button" style={s.btnMini} disabled={!!carico} onClick={() => fileRef.current.click()}>{carico ? `CARICO ${carico}…` : 'CARICA NUOVE'}</button>
              <button type="button" style={s.btnMini} onClick={() => setArchivio('scegli')}>SCEGLI DALL'ARCHIVIO</button>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginBottom: '14px' }}>
              {galleria.length > 1 ? `${galleria.length} foto: la prima (bordo arancio) è la copertina, ‹ › per cambiare l'ordine.` : 'Puoi mettere più foto: nel catalogo clienti scorrono come slide.'} Si salvano con il prodotto.
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={s.field}><label style={s.label}>NOME *</label>
                <input style={s.input} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} required /></div>

              <div style={s.field}><label style={s.label}>CATEGORIA</label>
                <select style={s.input} value={form.category_id} onChange={e => handleCatChange(e.target.value)}>
                  <option value="">— nessuna —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select></div>

              <div style={s.field}><label style={s.label}>FORNITORE</label>
                <select style={s.input} value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}>
                  <option value="">— nessuno —</option>
                  {suppliers.map(su => <option key={su.id} value={su.id}>{su.ragione_sociale}</option>)}
                </select></div>

              <div style={s.field}><label style={s.label}>DESCRIZIONE</label>
                <textarea style={{ ...s.input, resize: 'vertical', minHeight: '60px' }} value={form.descrizione} onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))} /></div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={s.field}><label style={s.label}>PREZZO VENDITA *</label>
                  <input style={s.input} type="number" step="0.01" value={form.prezzo_vendita_ivato} onChange={e => setForm(f => ({ ...f, prezzo_vendita_ivato: e.target.value }))} required /></div>
                <div style={s.field}><label style={s.label}>PREZZO ACQUISTO *</label>
                  <input style={s.input} type="number" step="0.01" value={form.prezzo_acquisto_imponibile} onChange={e => setForm(f => ({ ...f, prezzo_acquisto_imponibile: e.target.value }))} required /></div>
              </div>

              <div style={s.field}><label style={s.label}>IVA ACQUISTO %</label>
                <input style={s.input} type="number" value={form.iva_percentuale} onChange={e => setForm(f => ({ ...f, iva_percentuale: parseFloat(e.target.value) }))} /></div>

              {form.category_id && catSizes(form.category_id).length > 0 && (
                <div style={s.field}>
                  <label style={s.label}>TAGLIE</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {catSizes(form.category_id).map(t => (
                      <button key={t} type="button"
                        style={{ ...s.tagliaBtn, ...(form.taglie_attive.includes(t) ? s.tagliaBtnOn : {}) }}
                        onClick={() => toggleTaglia(t)}>{t}</button>
                    ))}
                  </div>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', marginTop: '4px' }}>
                    SKU: {form.taglie_attive.length > 0
                      ? form.taglie_attive.map(t => `${categories.find(c => c.id === parseInt(form.category_id))?.prefisso || 'PRD'}-${form.nome.toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,8) || 'NOME'}-${t}`).join(', ')
                      : '—'}
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input type="checkbox" id="pers" checked={form.personalizzabile} onChange={e => setForm(f => ({ ...f, personalizzabile: e.target.checked }))} style={{ accentColor: '#F4600C', width:'15px', height:'15px' }} />
                <label htmlFor="pers" style={{ ...s.label, marginBottom: 0 }}>STAMPA NOME DISPONIBILE (+€5 se richiesto in fase d'ordine)</label>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button type="button" style={s.btnSecondary} onClick={() => setShowPanel(false)}>ANNULLA</button>
                <button type="submit" style={{ ...s.btnPrimary, flex: 1 }} disabled={loading}>
                  {loading ? 'SALVATAGGIO...' : editProduct ? 'AGGIORNA' : 'CREA'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-title)', fontWeight: '800', letterSpacing: '2px' },
  sub: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', marginTop: '4px' },
  btnPrimary: { display: 'flex', alignItems: 'center', gap: '8px', background: '#F4600C', color:'#fff', border: 'none', borderRadius: '8px', padding: '11px 18px', fontSize: 'var(--font-size-xs)', fontWeight: '700', letterSpacing: '1px', cursor: 'pointer' },
  btnSecondary: { background: 'var(--border)', color: 'var(--text)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '10px 14px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  search: { width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '11px 14px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', outline: 'none', marginBottom: '20px', boxSizing: 'border-box' },
  catHeader: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px', cursor: 'pointer', marginBottom: '10px' },
  catName: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-sm)', fontWeight: '700', letterSpacing: '2px', flex: 1 },
  catCount: { fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' },
  cardImg: { height: '150px', background: 'var(--bg)', position: 'relative', overflow: 'hidden' },
  cardImgEl: { width: '100%', height: '100%', objectFit: 'cover' },
  cardImgPh: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  badge: { position: 'absolute', top: '6px', right: '6px', background: '#F4600C', color:'#fff', fontSize: 'var(--font-size-xs)', fontWeight: '700', padding: '2px 6px', borderRadius: '4px' },
  cardBody: { padding: '12px' },
  cardTop: { display: 'flex', justifyContent: 'space-between', marginBottom: '4px' },
  sku: { fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', fontWeight: '700', letterSpacing: '1px' },
  prodName: { fontSize: 'var(--font-size-base)', fontWeight: '700', marginBottom: '10px' },
  prices: { display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '8px 0', marginBottom: '10px' },
  priceL: { fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', letterSpacing: '1px', fontWeight: '700', marginBottom: '2px' },
  priceV: { fontSize: 'var(--font-size-base)', fontWeight: '700' },
  variants: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '10px' },
  varChip: { display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '4px 8px', minWidth: '36px' },
  varT: { fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', fontWeight: '700' },
  varQ: { fontSize: 'var(--font-size-sm)', fontWeight: '800' },
  actions: { display: 'flex', gap: '6px', borderTop: '1px solid var(--border)', paddingTop: '10px' },
  actionBtn: { display: 'flex', alignItems: 'center', gap: '4px', background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer', flex: 1, justifyContent: 'center' },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '60px 0', color: 'var(--text-dim)' },
  panel: { width: '360px', flexShrink: 0, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '12px', height: 'fit-content', position: 'sticky', top: '20px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' },
  panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' },
  panelTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-sm)', fontWeight: '700', letterSpacing: '2px' },
  closeBtn: { background: 'transparent', border: 'none', color: 'var(--text-dim)', cursor: 'pointer' },
  panelBody: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' },
  btnSec: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '10px', border: '1px solid var(--border2)', background: 'var(--bg-card)', color: 'var(--text)', fontWeight: '700', fontSize: 'var(--font-size-xs)', cursor: 'pointer', letterSpacing: '1px' },
  fotoBtn: { flex: 1, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)', padding: '2px 0', fontWeight: 700 },
  btnMini: { padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--border2)', background: 'var(--bg-input)', color: 'var(--text)', fontWeight: '700', fontSize: '11px', cursor: 'pointer' },
  imgUpload: { height: '140px', background: 'var(--bg-input)', border: '2px dashed var(--border2)', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: 'var(--font-size-xs)', letterSpacing: '2px', fontWeight: '700', color: 'var(--text-dim)' },
  input: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '9px 12px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', outline: 'none' },
  tagliaBtn: { padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border2)', background: 'var(--bg-input)', color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  tagliaBtnOn: { background: '#F4600C18', border: '1px solid #F4600C', color: '#F4600C' },
};
